import React, { useCallback, useEffect, useMemo, useState } from "react";
import API from "../services/api";
import {
  FLEET_FIRST_MACHINE_NO,
  FLEET_LAST_MACHINE_NO,
  FLEET_TARGET_MACHINES,
  FLEET_TARGET_TX_PER_MACHINE_DAY
} from "../constants/fleet";
import { formatDateTimeDMY } from "../utils/dateFormat";

const REFRESH_MS = 20000;
const KPI_TARGETS = {
  availabilityPct: 99.5,
  flowSuccessPct: 99.0,
  recoverySeconds: 30,
  detachPerHourMax: 0.5,
  wakeupOkPctMin: 98.0
};

function minutesSince(ts) {
  if (!ts) return Number.POSITIVE_INFINITY;
  const diff = Date.now() - new Date(ts).getTime();
  return Math.max(0, diff / 60000);
}

function formatAge(mins) {
  if (!Number.isFinite(mins)) return "sin señal";
  if (mins < 1) return "<1 min";
  if (mins < 60) return `${Math.round(mins)} min`;
  return `${(mins / 60).toFixed(1)} h`;
}

function healthLevelToColor(level) {
  if (level === "green") return "Verde";
  if (level === "yellow") return "Amarillo";
  return "Rojo";
}

function StatusMark({ level, size = "md" }) {
  return (
    <span
      className={`op-dot op-dot--${level} op-dot--${size}`}
      role="img"
      aria-label={healthLevelToColor(level)}
      title={healthLevelToColor(level)}
    />
  );
}

function connectionSummary(device, checks) {
  const byId = Object.fromEntries((checks || []).map(c => [c.id, c]));
  const lastTs = device.last_seen_at || device.qibixx_last_report_at || null;
  const ageMins = minutesSince(lastTs);
  const age = formatAge(ageMins);
  const lastStatus = String(device.qibixx_last_status || "").trim();
  const txFail = lastStatus.includes("USB TX failed");
  const detached =
    lastStatus.includes("USB detached") || lastStatus.includes("sin dispositivo");
  const usbState =
    device.qibixx_usb_connected == null
      ? "sin dato"
      : device.qibixx_usb_connected
        ? "el Moto ve el Qibixx"
        : "el Moto no ve el Qibixx";
  const level = overallLevel(checks);

  let headline;
  if (device.active === false) {
    headline = "este terminal está INACTIVO (no es una máquina en campo)";
  } else if (byId.backend?.level === "red" || byId.power?.level === "red") {
    headline = `el celular no reporta a la API (hace ${age})`;
  } else if (txFail) {
    headline = "USB enumerado, pero el Qibixx no escribe (MDB caído)";
  } else if (byId.usb?.level === "red" || detached) {
    headline = "USB desconectado: el Moto no ve el Qibixx";
  } else if (byId.mdb?.level === "red") {
    headline = "MDB / wakeup no responde";
  } else if (level === "yellow") {
    headline = "hay capas inestables; leé el detalle abajo";
  } else {
    headline = "celular, USB, Qibixx y MDB en línea";
  }

  const lines = [
    {
      label: "Celular",
      level: byId.backend?.level || "red",
      text: lastTs
        ? `última conexión hace ${age} (${formatDateTimeDMY(lastTs)})${
            device.reported_app_version
              ? ` · app ${device.reported_app_version}`
              : " · sin versión de app"
          }${device.reported_device_model ? ` · ${device.reported_device_model}` : ""}`
        : "nunca se conectó a la API"
    },
    {
      label: "Señal",
      level: byId.power?.level || "red",
      text:
        Number.isFinite(ageMins) && ageMins <= 10
          ? "reportando ahora (menos de 10 min)"
          : `sin señal reciente · ${age}`
    },
    {
      label: "USB",
      level: txFail ? "red" : byId.usb?.level || "yellow",
      text: txFail ? `${usbState}, pero no puede escribir (TX failed)` : usbState
    },
    {
      label: "Qibixx",
      level: txFail || detached ? "red" : lastStatus ? byId.mdb?.level || "yellow" : "yellow",
      text: lastStatus || "sin reporte del dongle"
    },
    {
      label: "MDB",
      level: byId.mdb?.level || "red",
      text: lastStatus.includes("USB wakeup(")
        ? lastStatus.includes(": ok")
          ? "wakeup OK"
          : "wakeup parcial o inestable"
        : txFail
          ? "no hay MDB: el USB no acepta frames"
          : "sin handshake / wakeup"
    }
  ];

  return { headline, lines, level };
}

function metricLevelGoodHigh(value, target) {
  if (value >= target) return "green";
  if (value >= target * 0.9) return "yellow";
  return "red";
}

function metricLevelGoodLow(value, maxAllowed) {
  if (value <= maxAllowed) return "green";
  if (value <= maxAllowed * 1.5) return "yellow";
  return "red";
}

function aggregateFleetMetrics(terminals, offlineThresholdHours) {
  if (!terminals.length) {
    return {
      activeTerminals: 0,
      projectedTxDay: 0,
      availabilityPct: 0,
      redCount: 0,
      yellowCount: 0,
      greenCount: 0
    };
  }
  let redCount = 0;
  let yellowCount = 0;
  let greenCount = 0;
  for (const t of terminals) {
    const checks = classifyChecks(t, offlineThresholdHours);
    const level = overallLevel(checks);
    if (level === "red") redCount += 1;
    else if (level === "yellow") yellowCount += 1;
    else greenCount += 1;
  }
  const availabilityPct = (greenCount / terminals.length) * 100;
  const projectedTxDay = terminals.length * FLEET_TARGET_TX_PER_MACHINE_DAY;
  return {
    activeTerminals: terminals.length,
    projectedTxDay,
    availabilityPct,
    redCount,
    yellowCount,
    greenCount
  };
}

function computeMathMetrics(device, historyRows, offlineThresholdHours) {
  const lastSignalTs = device.last_seen_at || device.qibixx_last_report_at || null;
  const signalAgeMins = minutesSince(lastSignalTs);
  const offlineMins = (offlineThresholdHours || 2) * 60;
  const freshnessScore = Number.isFinite(signalAgeMins)
    ? Math.max(0, Math.min(100, 100 - (signalAgeMins / offlineMins) * 100))
    : 0;

  if (!historyRows || historyRows.length === 0) {
    return {
      freshnessScore,
      telemetryEventsPerHour: 0,
      usbStabilityPct: 0,
      detachPerHour: 0,
      wakeupOkPct: 0,
      windowHours: 0
    };
  }

  const sorted = [...historyRows].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
  const start = new Date(sorted[0].created_at).getTime();
  const end = new Date(sorted[sorted.length - 1].created_at).getTime();
  const windowHours = Math.max((end - start) / 3600000, 1 / 60);

  let usbSamples = 0;
  let usbConnectedSamples = 0;
  let detachCount = 0;
  let wakeupCount = 0;
  let wakeupOkCount = 0;

  for (const row of sorted) {
    const payload = row?.payload && typeof row.payload === "object" ? row.payload : {};
    if (typeof payload.usb_connected === "boolean") {
      usbSamples += 1;
      if (payload.usb_connected) usbConnectedSamples += 1;
    }
    const status = String(payload.status_line || "");
    if (status.includes("USB detached")) {
      detachCount += 1;
    }
    if (status.includes("USB wakeup(")) {
      wakeupCount += 1;
      if (status.includes(": ok")) {
        wakeupOkCount += 1;
      }
    }
  }

  return {
    freshnessScore,
    telemetryEventsPerHour: sorted.length / windowHours,
    usbStabilityPct: usbSamples > 0 ? (usbConnectedSamples / usbSamples) * 100 : 0,
    detachPerHour: detachCount / windowHours,
    wakeupOkPct: wakeupCount > 0 ? (wakeupOkCount / wakeupCount) * 100 : 0,
    windowHours
  };
}

const VEND_HISTORY_WINDOW_HOURS = 4;

function statusLineImpliesVend(statusLine) {
  const s = String(statusLine || "").toUpperCase();
  return s.includes("VEND");
}

function historyContainsVend(historyRows, maxAgeHours) {
  if (!Array.isArray(historyRows) || historyRows.length === 0) return false;
  const cutoff = Date.now() - maxAgeHours * 3600000;
  const sorted = [...historyRows].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );
  for (const row of sorted) {
    const t = new Date(row.created_at).getTime();
    if (t < cutoff) break;
    const line = row?.payload && typeof row.payload === "object" ? row.payload.status_line : "";
    if (statusLineImpliesVend(line)) return true;
  }
  return false;
}

function classifyChecks(device, offlineThresholdHours, historyRows) {
  const lastSignalTs = device.last_seen_at || device.qibixx_last_report_at || null;
  const signalAgeMins = minutesSince(lastSignalTs);
  const offlineMins = (offlineThresholdHours || 2) * 60;
  const lastStatus = String(device.qibixx_last_status || "");
  const parseError = String(device.last_parse_error || "").trim();

  const checks = [];

  const signalLevel =
    !Number.isFinite(signalAgeMins) || signalAgeMins > offlineMins
      ? "red"
      : signalAgeMins > 10
        ? "yellow"
        : "green";
  checks.push({
    id: "power",
    title: "Power / Señal base",
    level: signalLevel,
    verifiers: [
      `Última señal: ${formatAge(signalAgeMins)}`,
      `Último reporte Qibixx: ${
        device.qibixx_last_report_at ? formatDateTimeDMY(device.qibixx_last_report_at) : "sin reporte"
      }`
    ]
  });

  const hasWakeup = lastStatus.includes("USB wakeup(");
  const hasTxFail = lastStatus.includes("USB TX failed");
  const hasDetached = lastStatus.includes("USB detached");
  const usbLevel = hasTxFail
    ? "red"
    : device.qibixx_usb_connected === true
      ? "green"
      : device.qibixx_usb_connected === false
        ? "red"
        : "yellow";
  checks.push({
    id: "usb",
    title: "USB Host Android",
    level: usbLevel,
    verifiers: [
      `USB reportado: ${
        device.qibixx_usb_connected == null ? "sin dato" : device.qibixx_usb_connected ? "conectado" : "desconectado"
      }`,
      hasTxFail
        ? "Enumerado, pero TX failed: el Moto no puede escribir al Qibixx"
        : `Modelo reportado: ${device.reported_device_model || "sin dato"}`
    ]
  });
  const mdbLevel = hasTxFail || hasDetached ? "red" : hasWakeup ? "green" : device.qibixx_usb_connected ? "yellow" : "red";
  checks.push({
    id: "mdb",
    title: "MDB Handshake / Wakeup",
    level: mdbLevel,
    verifiers: [
      `Último estado: ${lastStatus ? lastStatus.slice(0, 80) : "sin línea de estado"}`,
      `Wakeup detectado: ${hasWakeup ? "sí" : "no"}`
    ]
  });

  const hasVendLast = statusLineImpliesVend(lastStatus);
  const historyLoaded = historyRows !== undefined && historyRows !== null;
  const hasVendInWindow = historyLoaded
    ? historyContainsVend(historyRows, VEND_HISTORY_WINDOW_HOURS)
    : false;
  const hasVend = hasVendLast || hasVendInWindow;
  // `last_parse_error` puede quedar “pegado” por ruido USB (bytes basura) aunque hoy el enlace esté OK.
  // Solo lo consideramos rojo si además la última señal es reciente; si no, mostramos amarillo.
  const parseErrorActive =
    parseError && Number.isFinite(signalAgeMins) && signalAgeMins <= 10;
  const vendLevel = parseErrorActive ? "red" : hasVend ? "green" : "yellow";
  checks.push({
    id: "vend",
    title: "VEND / Parser",
    level: vendLevel,
    verifiers: [
      `VEND en última línea: ${hasVendLast ? "sí" : "no"}`,
      historyLoaded
        ? `VEND en historial (${VEND_HISTORY_WINDOW_HOURS}h): ${hasVendInWindow ? "sí" : "no"}`
        : `VEND en historial: sin datos (lista o cargando)`,
      `parse_error: ${parseError ? parseError.slice(0, 80) : "sin errores"}`
    ]
  });

  const backendLevel = signalAgeMins > offlineMins ? "red" : signalAgeMins > 5 ? "yellow" : "green";
  checks.push({
    id: "backend",
    title: "Backend Sync",
    level: backendLevel,
    verifiers: [
      `last_seen / reporte: ${lastSignalTs ? formatDateTimeDMY(lastSignalTs) : "sin sincronización"}`,
      `Versión app: ${device.reported_app_version || "sin dato"}`
    ]
  });

  const hasWatchdog = lastStatus.includes("USB watchdog");
  const recoveryLevel = hasWatchdog ? "green" : device.qibixx_usb_connected ? "yellow" : "red";
  checks.push({
    id: "autorecovery",
    title: "Autorecuperación",
    level: recoveryLevel,
    verifiers: [
      `Watchdog reportado: ${hasWatchdog ? "sí" : "no"}`,
      `Comando remoto recomendado: ${device.qibixx_usb_connected ? "REOPEN_USB si persiste" : "reintentar conexión USB"}`
    ]
  });

  const lastTxAt = device.last_tx_event_at || null;
  const lastTxType = device.last_tx_event_type || "";
  const lastTxReason = String(device.last_tx_event_reason || "");
  const txAgeMin = lastTxAt ? minutesSince(lastTxAt) : Number.POSITIVE_INFINITY;
  const qibTelemMin = device.qibixx_last_report_at
    ? minutesSince(device.qibixx_last_report_at)
    : Number.POSITIVE_INFINITY;
  const telemetryFresh =
    Number.isFinite(qibTelemMin) && qibTelemMin >= 0 && qibTelemMin < 45;
  const eventsQuiet =
    !Number.isFinite(txAgeMin) || txAgeMin > 120 || !lastTxAt;
  let appApiLevel = "green";
  if (
    lastTxType === "payment_rejected" &&
    lastTxReason.toLowerCase().includes("log in")
  ) {
    appApiLevel = "red";
  } else if (telemetryFresh && eventsQuiet) {
    appApiLevel = "yellow";
  } else if (
    !lastTxAt &&
    Number.isFinite(signalAgeMins) &&
    signalAgeMins < 60
  ) {
    appApiLevel = "yellow";
  }
  checks.push({
    id: "app_tx_events",
    title: "App → API (eventos de pago)",
    level: appApiLevel,
    verifiers: [
      `Último evento: ${lastTxType || "—"} @ ${lastTxAt ? formatDateTimeDMY(lastTxAt) : "ninguno aún"}`,
      lastTxReason
        ? `Motivo / detalle: ${lastTxReason.slice(0, 120)}`
        : "Sin texto de motivo en el último evento",
      `Últimas 24h (BD): vend_hold ${Number(device.tx_vend_hold_24h ?? 0)} · payment_started ${Number(device.tx_start_24h ?? 0)} · payment_approved ${Number(device.tx_approve_24h ?? 0)} · payment_rejected ${Number(device.tx_reject_24h ?? 0)}`,
      telemetryFresh && eventsQuiet
        ? "Telemetría Qibixx reciente pero sin eventos de app recientes: revisar Wi‑Fi hacia el API, parser de VEND o que la app esté en primer plano."
        : "Si hubo compra en máquina y aquí no sube vend_hold/payment_started, no llegó VEND reconocido por USB o no hay ruta HTTP al backend."
    ]
  });

  return checks;
}

function overallLevel(checks) {
  if (checks.some(c => c.level === "red")) return "red";
  if (checks.some(c => c.level === "yellow")) return "yellow";
  return "green";
}

function formatBytes(n) {
  if (n == null || !Number.isFinite(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function OperationsControl() {
  const [fleet, setFleet] = useState(null);
  const [fleetError, setFleetError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [historyRows, setHistoryRows] = useState(null);
  const [transactionEvents, setTransactionEvents] = useState(null);
  const [sessionReports, setSessionReports] = useState(null);
  const [reopenBusy, setReopenBusy] = useState(false);
  /** true: listar también dispositivos inactivos (evita lista vacía si solo hay inactivos). */
  const [showInactive, setShowInactive] = useState(true);
  const [deviceActiveBusy, setDeviceActiveBusy] = useState(false);
  const [apkInfo, setApkInfo] = useState(null);
  const [apkError, setApkError] = useState(null);
  const [apkLoading, setApkLoading] = useState(true);
  const [apkDownloadBusy, setApkDownloadBusy] = useState(false);
  const [supportTickets, setSupportTickets] = useState([]);
  const [supportBusyId, setSupportBusyId] = useState(null);
  const [doctor, setDoctor] = useState(null);

  const load = useCallback(async () => {
    try {
      setFleetError(null);
      const res = await API.get("/devices/fleet");
      setFleet(res.data);
      setSelectedId(prev => {
        const visible = (res.data?.terminals || []).filter(
          t => showInactive || t.active !== false
        );
        if (!visible.length) return null;
        if (prev != null && visible.some(t => t.id === prev)) return prev;
        return visible[0].id;
      });
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        "No se pudo cargar /devices/fleet";
      setFleetError(msg);
      setFleet(null);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const fetchApkInfo = useCallback(async () => {
    setApkLoading(true);
    setApkError(null);
    try {
      const res = await API.get("/releases/info");
      setApkInfo(res.data);
    } catch (e) {
      setApkInfo(null);
      setApkError(e?.response?.data?.error || e?.response?.data?.hint || e?.message || "Sin repositorio APK");
    } finally {
      setApkLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApkInfo();
  }, [fetchApkInfo]);

  const loadSupportTickets = useCallback(async () => {
    try {
      const res = await API.get("/support/tickets", { params: { status: "open" } });
      setSupportTickets(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      setSupportTickets([]);
    }
  }, []);

  useEffect(() => {
    loadSupportTickets();
    const t = setInterval(loadSupportTickets, REFRESH_MS);
    return () => clearInterval(t);
  }, [loadSupportTickets]);

  useEffect(() => {
    if (selectedId == null) {
      setDoctor(null);
      return undefined;
    }
    let cancelled = false;
    const pull = async () => {
      try {
        const res = await API.get(`/devices/${selectedId}/doctor`);
        if (!cancelled) setDoctor(res.data);
      } catch (_) {
        if (!cancelled) setDoctor(null);
      }
    };
    pull();
    const t = setInterval(pull, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [selectedId]);

  const updateSupportTicket = useCallback(
    async (id, status) => {
      setSupportBusyId(id);
      try {
        await API.patch(`/support/tickets/${id}`, { status });
        await loadSupportTickets();
      } catch (e) {
        alert(e?.response?.data?.error || "No se pudo actualizar el ticket");
      } finally {
        setSupportBusyId(null);
      }
    },
    [loadSupportTickets]
  );

  const downloadLatestApk = useCallback(async () => {
    if (!apkInfo?.available) return;
    setApkDownloadBusy(true);
    try {
      const res = await API.get("/releases/download", { responseType: "blob" });
      const ct = (res.headers["content-type"] || "").toLowerCase();
      if (ct.includes("application/json")) {
        const text = await res.data.text();
        let msg = "Error al descargar";
        try {
          const j = JSON.parse(text);
          if (j.error) msg = j.error;
        } catch (_) {
          /* ignore */
        }
        throw new Error(msg);
      }
      const filename = apkInfo?.filename || "vikingo2.apk";
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.message || "No se pudo descargar el APK");
    } finally {
      setApkDownloadBusy(false);
    }
  }, [apkInfo]);

  useEffect(() => {
    if (!selectedId) {
      setHistoryRows(null);
      return;
    }
    setHistoryRows(null);
    let cancelled = false;
    API.get("/devices/qibixx/history", { params: { device_id: selectedId, limit: 120 } })
      .then(res => {
        if (!cancelled) {
          setHistoryRows(Array.isArray(res.data) ? res.data : []);
        }
      })
      .catch(() => {
        if (!cancelled) setHistoryRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, fleet?.terminals?.length]);

  useEffect(() => {
    if (!selectedId) {
      setTransactionEvents(null);
      return;
    }
    let cancelled = false;
    const fetchTxEvents = () => {
      API.get("/devices/transaction-events", {
        params: { device_id: selectedId, limit: 80 }
      })
        .then(res => {
          if (!cancelled) {
            setTransactionEvents(Array.isArray(res.data) ? res.data : []);
          }
        })
        .catch(() => {
          if (!cancelled) setTransactionEvents([]);
        });
    };
    fetchTxEvents();
    const interval = setInterval(fetchTxEvents, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) {
      setSessionReports(null);
      return;
    }
    let cancelled = false;
    const fetchSessionReports = () => {
      API.get("/devices/session-reports", { params: { device_id: selectedId, limit: 8 } })
        .then(res => {
          if (!cancelled) {
            setSessionReports(Array.isArray(res.data) ? res.data : []);
          }
        })
        .catch(() => {
          if (!cancelled) setSessionReports([]);
        });
    };
    fetchSessionReports();
    const interval = setInterval(fetchSessionReports, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [selectedId]);

  const enqueueReopenUsb = useCallback(async () => {
    if (!selectedId) return;
    setReopenBusy(true);
    try {
      await API.post(`/devices/${selectedId}/commands`, { command_type: "REOPEN_USB" });
      await load();
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo encolar REOPEN_USB");
    } finally {
      setReopenBusy(false);
    }
  }, [selectedId, load]);

  const enqueueForegroundApp = useCallback(async () => {
    if (!selectedId) return;
    try {
      await API.post(`/devices/${selectedId}/commands`, { command_type: "FOREGROUND_APP" });
      await load();
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo traer Vikingo al frente");
    }
  }, [selectedId, load]);

  const enqueueSumupLogin = useCallback(async () => {
    if (!selectedId) return;
    try {
      await API.post(`/devices/${selectedId}/commands`, { command_type: "SUMUP_LOGIN" });
      await load();
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo abrir el login SumUp");
    }
  }, [selectedId, load]);

  const enqueueUpdateApp = useCallback(async () => {
    if (!selectedId) return;
    try {
      await API.post(`/devices/${selectedId}/commands`, { command_type: "UPDATE_APP" });
      await load();
    } catch (e) {
      alert(e?.response?.data?.error || "No se pudo encolar la actualización");
    }
  }, [selectedId, load]);

  const toggleRemoteUsbDiag = useCallback(
    async value => {
      if (!selectedId || !fleet) return;
      const d = (fleet.terminals || []).find(t => t.id === selectedId);
      if (!d) return;
      try {
        await API.put(`/devices/${d.id}`, {
          name: d.name,
          machine_id: d.machine_id,
          active: d.active,
          qibixx_registration_id: d.qibixx_registration_id ?? null,
          remote_config: { usb_diagnostic_log: value }
        });
        await load();
      } catch (e) {
        alert(e?.response?.data?.error || "No se pudo actualizar config");
      }
    },
    [selectedId, fleet, load]
  );

  const setDeviceActive = useCallback(
    async nextActive => {
      if (!selectedId || !fleet) return;
      const d = (fleet.terminals || []).find(t => t.id === selectedId);
      if (!d) return;
      const msg = nextActive
        ? "¿Reactivar este terminal en Control operativo?"
        : "¿Desactivar este terminal? Ya no aparecerá en la flota operativa. Útil si el mismo Qibixx / Moto pasó a otra máquina (p. ej. 1026) y este registro quedó obsoleto.";
      if (!window.confirm(msg)) return;
      setDeviceActiveBusy(true);
      try {
        await API.put(`/devices/${d.id}`, {
          name: d.name,
          machine_id: d.machine_id,
          active: nextActive,
          qibixx_registration_id: d.qibixx_registration_id ?? null,
          remote_config:
            d.remote_config && typeof d.remote_config === "object" && !Array.isArray(d.remote_config)
              ? d.remote_config
              : {}
        });
        await load();
      } catch (e) {
        alert(e?.response?.data?.error || "No se pudo actualizar el dispositivo");
      } finally {
        setDeviceActiveBusy(false);
      }
    },
    [selectedId, fleet, load]
  );

  const terminalsAll = useMemo(() => fleet?.terminals || [], [fleet]);
  const terminals = useMemo(() => {
    const vis = terminalsAll.filter(t => showInactive || t.active !== false);
    return [...vis].sort((a, b) => {
      if (!!a.active === !!b.active) return a.id - b.id;
      return a.active ? -1 : 1;
    });
  }, [terminalsAll, showInactive]);

  const terminalsOperationalOnly = useMemo(
    () => terminalsAll.filter(t => t.active !== false),
    [terminalsAll]
  );

  const selected = terminals.find(t => t.id === selectedId) || null;

  const checks = useMemo(() => {
    if (!selected) return [];
    return classifyChecks(selected, fleet?.offline_threshold_hours, historyRows);
  }, [selected, fleet, historyRows]);

  const layerSummary = useMemo(
    () => (selected ? connectionSummary(selected, checks) : null),
    [selected, checks]
  );

  const metrics = useMemo(() => {
    if (!selected) return null;
    return computeMathMetrics(selected, historyRows, fleet?.offline_threshold_hours);
  }, [selected, historyRows, fleet]);

  const fleetMetrics = useMemo(
    () =>
      aggregateFleetMetrics(terminalsOperationalOnly, fleet?.offline_threshold_hours),
    [terminalsOperationalOnly, fleet]
  );

  const flowSuccessPct = metrics
    ? Math.max(
        0,
        Math.min(
          100,
          // Proxy de éxito operativo: USB estable + wakeup OK.
          0.6 * metrics.usbStabilityPct + 0.4 * metrics.wakeupOkPct
        )
      )
    : 0;
  const recoverySecondsEstimate = metrics
    ? Math.min(120, metrics.detachPerHour > 0 ? 60 / metrics.detachPerHour : 10)
    : 120;

  return (
    <div>
      <header className="page-header">
        <h1 className="page-title">Control Operativo Vikingo</h1>
        <p className="page-subtitle">
          Semáforo por terminal y por capa. El doctor corre cada 60 s: intenta recuperar solo
          (USB, con tope) y, si no puede, te deja el paso a paso. Refresco de pantalla cada{" "}
          {Math.round(REFRESH_MS / 1000)} s.
        </p>
      </header>

      <section
        className="card card--padded"
        style={{
          marginBottom: "1rem",
          borderLeft: "4px solid var(--color-warning)"
        }}
      >
        <h2 className="section-title" style={{ marginTop: 0 }}>
          Soporte QR / WhatsApp
          {supportTickets.length > 0 ? (
            <span
              style={{
                marginLeft: "0.5rem",
                background: "var(--color-danger)",
                color: "#fff",
                borderRadius: "999px",
                padding: "0.15rem 0.55rem",
                fontSize: "0.8rem"
              }}
            >
              {supportTickets.length} abiertos
            </span>
          ) : null}
        </h2>
        <p style={{ margin: "0 0 0.75rem", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
          Tickets desde el menú del QR pegado en la máquina. Devoluciones: revisar logs de venta /
          despacho antes de autorizar entrega de emergencia (cámara remota en una fase siguiente;
          no mezclar con SumUp en cobro).
        </p>
        {!supportTickets.length ? (
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>Sin tickets abiertos.</p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0.65rem" }}>
            {supportTickets.map(t => (
              <div
                key={t.id}
                style={{
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-sm)",
                  padding: "0.65rem 0.75rem",
                  background:
                    t.category === "devoluciones"
                      ? "rgba(220, 38, 38, 0.06)"
                      : "var(--color-bg-elevated)"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    gap: "0.5rem"
                  }}
                >
                  <strong>
                    #{t.id} · {t.category}
                    {t.machine_name ? ` · ${t.machine_name}` : ""}
                  </strong>
                  <span style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                    {formatDateTimeDMY(t.created_at)}
                  </span>
                </div>
                {t.missing_product ? (
                  <div style={{ fontSize: "0.9rem", marginTop: "0.25rem" }}>
                    Producto faltante: {t.missing_product}
                  </div>
                ) : null}
                {t.improvement_text ? (
                  <div style={{ fontSize: "0.9rem", marginTop: "0.25rem" }}>
                    Mejora: {t.improvement_text}
                  </div>
                ) : null}
                {t.message ? (
                  <div style={{ fontSize: "0.9rem", marginTop: "0.25rem" }}>{t.message}</div>
                ) : null}
                {t.charged_card === true || t.charged_card === false ? (
                  <div style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                    ¿Cobró tarjeta?: {t.charged_card ? "Sí" : "No"}
                  </div>
                ) : null}
                <div style={{ marginTop: "0.5rem", display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    disabled={supportBusyId === t.id}
                    onClick={() => updateSupportTicket(t.id, "in_progress")}
                  >
                    En curso
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={supportBusyId === t.id}
                    onClick={() => updateSupportTicket(t.id, "resolved")}
                  >
                    Resuelto
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section
        className="card card--padded"
        style={{ marginBottom: "1rem", borderLeft: "4px solid var(--color-accent)" }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "1rem"
          }}
        >
          <div>
            <h2 className="section-title" style={{ marginTop: 0 }}>
              Repositorio APK (terreno)
            </h2>
            <p style={{ margin: "0.25rem 0 0", color: "var(--color-text-muted)", fontSize: "0.9rem", maxWidth: "42rem" }}>
              Misma versión que debe llevar el Moto de cada máquina. Descarga con sesión del panel;
              en el servidor: carpeta <code style={{ fontSize: "0.85em" }}>02_BACKEND_DOCKER/releases/</code>{" "}
              con <code style={{ fontSize: "0.85em" }}>manifest.json</code> y el archivo indicado en{" "}
              <code style={{ fontSize: "0.85em" }}>filename</code> (ver <code style={{ fontSize: "0.85em" }}>manifest.example.json</code>).
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.5rem" }}>
            {apkLoading ? (
              <span style={{ color: "var(--color-text-muted)", fontSize: "0.9rem" }}>Comprobando…</span>
            ) : apkError ? (
              <span style={{ color: "var(--color-danger)", fontSize: "0.9rem", maxWidth: "320px", textAlign: "right" }}>
                {apkError}
              </span>
            ) : apkInfo ? (
              <>
                <div style={{ textAlign: "right", fontSize: "0.9rem" }}>
                  <strong>
                    {apkInfo.version_name || "—"}
                    {apkInfo.version_code != null ? ` (${apkInfo.version_code})` : ""}
                  </strong>
                  <div style={{ color: "var(--color-text-muted)" }}>
                    {apkInfo.filename}
                    {apkInfo.size_bytes != null ? ` · ${formatBytes(apkInfo.size_bytes)}` : ""}
                  </div>
                  {apkInfo.updated_at ? (
                    <div style={{ color: "var(--color-text-muted)" }}>
                      Publicado: {formatDateTimeDMY(apkInfo.updated_at)}
                    </div>
                  ) : null}
                  {apkInfo.notes ? (
                    <div style={{ color: "var(--color-text-secondary)", marginTop: "0.35rem", maxWidth: "360px" }}>
                      {apkInfo.notes}
                    </div>
                  ) : null}
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!apkInfo.available || apkDownloadBusy}
                    onClick={downloadLatestApk}
                  >
                    {apkDownloadBusy
                      ? "Descargando…"
                      : apkInfo.available
                        ? "Descargar APK"
                        : "APK no disponible en servidor"}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={fetchApkInfo}>
                    Actualizar info
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </section>

      <div className="dashboard-kpi-grid" style={{ marginBottom: "1rem" }}>
        <div className="kpi-card">
          <p className="kpi-card__label">Terminales activas</p>
          <p className="kpi-card__value">{fleetMetrics.activeTerminals}</p>
          <p className="kpi-card__hint">
            Meta: {FLEET_TARGET_MACHINES} (N° {FLEET_FIRST_MACHINE_NO}–{FLEET_LAST_MACHINE_NO})
          </p>
        </div>
        <div className="kpi-card">
          <p className="kpi-card__label">Capacidad objetivo</p>
          <p className="kpi-card__value">{(FLEET_TARGET_MACHINES * FLEET_TARGET_TX_PER_MACHINE_DAY).toLocaleString()}</p>
          <p className="kpi-card__hint">
            tx/día ({FLEET_TARGET_MACHINES} × {FLEET_TARGET_TX_PER_MACHINE_DAY})
          </p>
        </div>
        <div className="kpi-card">
          <p className="kpi-card__label">Capacidad actual proyectada</p>
          <p className="kpi-card__value">{fleetMetrics.projectedTxDay.toLocaleString()}</p>
          <p className="kpi-card__hint">tx/día con activas actuales</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-card__label">Disponibilidad flota</p>
          <p className="kpi-card__value">{fleetMetrics.availabilityPct.toFixed(1)}%</p>
          <p className="kpi-card__hint">
            objetivo {KPI_TARGETS.availabilityPct}% · G/Y/R {fleetMetrics.greenCount}/{fleetMetrics.yellowCount}/{fleetMetrics.redCount}
          </p>
        </div>
      </div>

      <div className="op-layout">
        <section className="card card--padded">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "0.75rem",
              flexWrap: "wrap",
              marginBottom: "0.5rem"
            }}
          >
            <h2 className="section-title" style={{ margin: 0 }}>
              Terminales
            </h2>
            <label
              style={{
                fontSize: "0.8rem",
                display: "flex",
                alignItems: "center",
                gap: "0.35rem",
                cursor: "pointer",
                userSelect: "none"
              }}
            >
              <input
                type="checkbox"
                checked={showInactive}
                onChange={e => setShowInactive(e.target.checked)}
              />
              Mostrar inactivos
            </label>
          </div>
          <p style={{ margin: "0 0 0.75rem", fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
            Cada fila es un <strong>dispositivo</strong> registrado en{" "}
            <strong>Dispositivos</strong> (nombre en base de datos). &quot;Vikingo&quot; es el
            panel; el teléfono aparece con el nombre que le diste al alta.
          </p>
          {fleetError ? (
            <div
              role="alert"
              style={{
                padding: "0.75rem",
                borderRadius: "6px",
                background: "rgba(220, 53, 69, 0.12)",
                color: "var(--color-danger, #b91c1c)",
                fontSize: "0.9rem"
              }}
            >
              <strong>Error al cargar terminales.</strong> {fleetError}
              <div style={{ marginTop: "0.5rem", fontSize: "0.85rem", opacity: 0.95 }}>
                Comprobá que el API esté en marcha ({String(process.env.REACT_APP_API_URL || "http://localhost:4000")}
                ), que hayas iniciado sesión y que el token no haya expirado.
              </div>
            </div>
          ) : null}
          {loading && terminals.length === 0 && !fleetError ? (
            <p>Cargando…</p>
          ) : !fleetError && !loading && terminals.length === 0 ? (
            <div
              style={{
                padding: "0.75rem",
                borderRadius: "6px",
                background: "var(--color-bg-muted, rgba(0,0,0,0.04))",
                fontSize: "0.9rem",
                lineHeight: 1.45
              }}
            >
              {terminalsAll.length > 0 ? (
                <>
                  <strong>No hay terminales activos en la lista.</strong> Hay{" "}
                  {terminalsAll.filter(t => t.active === false).length} registro(s) marcado(s) como{" "}
                  <em>inactivo</em>. Marcá <strong>Mostrar inactivos</strong> arriba o reactivá el
                  dispositivo desde esta pantalla al seleccionarlo.
                </>
              ) : (
                <>
                  <strong>No hay dispositivos para tu empresa.</strong> Creá uno en{" "}
                  <strong>Dispositivos</strong> (mismo usuario / compañía) o verificá en la base que
                  el Moto tenga <code>company_id</code> correcto.
                </>
              )}
            </div>
          ) : null}
          {!fleetError && !loading && terminals.length > 0 ? (
            <div className="op-terminal-list">
              {terminals.map(t => {
                const tChecks = classifyChecks(t, fleet?.offline_threshold_hours);
                const level = overallLevel(tChecks);
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`op-terminal-item${selectedId === t.id ? " op-terminal-item--active" : ""}${
                      t.active === false ? " op-terminal-item--inactive" : ""
                    }`}
                    onClick={() => setSelectedId(t.id)}
                  >
                    <StatusMark level={level} size="sm" />
                    <span>
                      <strong>{t.name}</strong>
                      <small>
                        #{t.id} · {t.machine_name || "sin máquina"} · {healthLevelToColor(level)}
                        {t.active === false ? " · INACTIVO" : ""}
                        {(t.alerts || []).length > 0
                          ? ` · ${t.alerts.join(", ")}`
                          : ""}
                      </small>
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}
        </section>

        <section className="card card--padded">
          {!selected ? (
            <p>Selecciona un terminal para ver verificadores.</p>
          ) : (
            <>
              <h2 className="section-title" style={{ display: "flex", alignItems: "center", gap: "0.65rem", flexWrap: "wrap" }}>
                {selected.name} (#{selected.id}) - Estado {healthLevelToColor(overallLevel(checks))}
                <StatusMark level={overallLevel(checks)} size="lg" />
              </h2>
              <p style={{ marginTop: 0, color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                Máquina: {selected.machine_name || "sin asignar"}
              </p>
              {layerSummary ? (
                <div className={`op-summary op-summary--${layerSummary.level}`}>
                  <p className="op-summary__headline">
                    <StatusMark level={layerSummary.level} size="lg" />
                    <span>{layerSummary.headline}</span>
                  </p>
                  <ul className="op-summary__lines">
                    {layerSummary.lines.map(line => (
                      <li key={line.label}>
                        <span className="op-summary__label">{line.label}</span>
                        <StatusMark level={line.level} size="sm" />
                        <span>{line.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {doctor ? (
                <div className={`op-doctor op-summary--${doctor.overall || "yellow"}`}>
                  <h3 className="op-doctor__title">Doctor Vikingo</h3>
                  <p className="op-doctor__headline">{doctor.headline}</p>
                  {doctor.silence_cause ? (
                    <p className="op-doctor__cause">
                      <strong>Causa más probable:</strong> {doctor.silence_cause.headline}
                      {doctor.silence_cause.confidence
                        ? ` (confianza ${doctor.silence_cause.confidence})`
                        : ""}
                      {doctor.silence_cause.detail ? (
                        <>
                          <br />
                          {doctor.silence_cause.detail}
                        </>
                      ) : null}
                    </p>
                  ) : null}
                  <p className="op-doctor__meta">
                    Checklist cada {doctor.interval_seconds || 60} s · {doctor.auto_note}
                    {doctor.last_auto
                      ? ` · Último auto: ${doctor.last_auto.command_type} (${doctor.last_auto.status}) ${
                          doctor.last_auto.created_at
                            ? formatDateTimeDMY(doctor.last_auto.created_at)
                            : ""
                        }`
                      : " · Sin comando automático aún"}
                  </p>
                  <ul className="op-doctor__checks">
                    {(doctor.items || []).map(item => (
                      <li key={item.id}>
                        <StatusMark level={item.level || "yellow"} size="sm" />
                        <div>
                          <strong>{item.title}</strong>
                          <span className="op-doctor__auto">
                            {item.auto === "ok"
                              ? "OK"
                              : item.auto === "reopen"
                                ? "Auto: reabrir USB"
                                : item.auto === "app"
                                  ? "Auto: app en el Moto"
                                  : item.auto === "espera"
                                    ? "Espera"
                                    : "Manual"}
                          </span>
                          <div>{item.detail}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <h4 className="op-doctor__steps-title">Qué hacer si no se corrige solo</h4>
                  {doctor.field_steps && doctor.field_steps.length ? (
                    <ol className="op-doctor__steps">
                      {doctor.field_steps.map(step => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                  ) : (
                    <p className="op-doctor__meta">Nada que hacer en campo en este momento.</p>
                  )}
                </div>
              ) : null}

              <div
                className="field-row"
                style={{ flexWrap: "wrap", gap: "0.5rem", alignItems: "center", marginBottom: "0.75rem" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={reopenBusy}
                  onClick={enqueueReopenUsb}
                >
                  {reopenBusy ? "Enviando…" : "Reabrir USB (REOPEN_USB)"}
                </button>
                <button type="button" className="btn btn-secondary" onClick={enqueueForegroundApp}>
                  Traer Vikingo al frente
                </button>
                <button type="button" className="btn btn-secondary" onClick={enqueueSumupLogin}>
                  Abrir login SumUp
                </button>
                <button type="button" className="btn btn-primary" onClick={enqueueUpdateApp}>
                  Actualizar app
                </button>
                {selected.active !== false ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={deviceActiveBusy}
                    onClick={() => setDeviceActive(false)}
                    style={{ borderColor: "var(--color-danger, #b91c1c)", color: "var(--color-danger, #b91c1c)" }}
                  >
                    {deviceActiveBusy ? "Guardando…" : "Desactivar en operaciones"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={deviceActiveBusy}
                    onClick={() => setDeviceActive(true)}
                  >
                    {deviceActiveBusy ? "Guardando…" : "Reactivar terminal"}
                  </button>
                )}
                <label
                  style={{
                    fontSize: "0.8rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.35rem",
                    cursor: "pointer",
                    whiteSpace: "nowrap"
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(selected.remote_config_effective?.usb_diagnostic_log)}
                    onChange={e => toggleRemoteUsbDiag(e.target.checked)}
                  />
                  Diag. USB remoto
                </label>
                <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", maxWidth: "360px" }}>
                  Reintenta Moto ↔ Qibixx; el log extra ayuda a ver fallos de puerto. No corrige MDB
                  ni alimentación de la Fuji.
                </span>
              </div>

              <div className="dashboard-kpi-grid" style={{ marginBottom: "1rem" }}>
                <div className="kpi-card">
                  <p className="kpi-card__label">Último evento (app)</p>
                  <p className="kpi-card__value" style={{ fontSize: "0.95rem" }}>
                    {selected.last_tx_event_type || "—"}
                  </p>
                  <p className="kpi-card__hint">
                    {selected.last_tx_event_at
                      ? formatDateTimeDMY(selected.last_tx_event_at)
                      : "sin registros en transaction_events"}
                  </p>
                </div>
                <div className="kpi-card">
                  <p className="kpi-card__label">vend_hold (24h)</p>
                  <p className="kpi-card__value">{Number(selected.tx_vend_hold_24h ?? 0)}</p>
                  <p className="kpi-card__hint">debería subir al elegir producto</p>
                </div>
                <div className="kpi-card">
                  <p className="kpi-card__label">Pagos iniciados / OK / rechazo (24h)</p>
                  <p className="kpi-card__value">
                    {Number(selected.tx_start_24h ?? 0)} / {Number(selected.tx_approve_24h ?? 0)} /{" "}
                    {Number(selected.tx_reject_24h ?? 0)}
                  </p>
                  <p className="kpi-card__hint">SumUp + backend</p>
                </div>
                <div className="kpi-card">
                  <p className="kpi-card__label">Alertas servidor</p>
                  <p className="kpi-card__value" style={{ fontSize: "0.85rem" }}>
                    {(selected.alerts || []).length ? (selected.alerts || []).join(", ") : "ninguna"}
                  </p>
                  <p className="kpi-card__hint">sumup_not_logged_in, app_api_silent, …</p>
                </div>
              </div>

              {metrics && (
                <div className="dashboard-kpi-grid" style={{ marginBottom: "1rem" }}>
                  <div className="kpi-card">
                    <p className="kpi-card__label">Score señal</p>
                    <p className="kpi-card__value">{metrics.freshnessScore.toFixed(1)}%</p>
                    <p className="kpi-card__hint">100% = señal reciente</p>
                  </div>
                  <div className="kpi-card">
                    <p className="kpi-card__label">Estabilidad USB</p>
                    <p className="kpi-card__value">{metrics.usbStabilityPct.toFixed(1)}%</p>
                    <p className="kpi-card__hint">muestras usb_connected=true</p>
                  </div>
                  <div className="kpi-card">
                    <p className="kpi-card__label">Eventos / hora</p>
                    <p className="kpi-card__value">{metrics.telemetryEventsPerHour.toFixed(2)}</p>
                    <p className="kpi-card__hint">telemetría Qibixx</p>
                  </div>
                  <div className="kpi-card">
                    <p className="kpi-card__label">Detach / hora</p>
                    <p className="kpi-card__value">{metrics.detachPerHour.toFixed(2)}</p>
                    <p className="kpi-card__hint">ideal cercano a 0</p>
                  </div>
                  <div className="kpi-card">
                    <p className="kpi-card__label">Wakeup OK</p>
                    <p className="kpi-card__value">{metrics.wakeupOkPct.toFixed(1)}%</p>
                    <p className="kpi-card__hint">objetivo {KPI_TARGETS.wakeupOkPctMin}%</p>
                  </div>
                  <div className="kpi-card">
                    <p className="kpi-card__label">Ventana analizada</p>
                    <p className="kpi-card__value">{metrics.windowHours.toFixed(2)}h</p>
                    <p className="kpi-card__hint">
                      {historyRows == null ? "…" : historyRows.length} eventos
                    </p>
                  </div>
                  <div className="kpi-card">
                    <p className="kpi-card__label">Éxito de flujo (proxy)</p>
                    <p className="kpi-card__value">{flowSuccessPct.toFixed(1)}%</p>
                    <p className="kpi-card__hint">objetivo {KPI_TARGETS.flowSuccessPct}%</p>
                  </div>
                  <div className="kpi-card">
                    <p className="kpi-card__label">Recuperación estimada</p>
                    <p className="kpi-card__value">{recoverySecondsEstimate.toFixed(0)}s</p>
                    <p className="kpi-card__hint">objetivo ≤ {KPI_TARGETS.recoverySeconds}s</p>
                  </div>
                  <div className="kpi-card">
                    <p className="kpi-card__label">Cumplimiento SLO</p>
                    <p className="kpi-card__value">
                      {[
                        metricLevelGoodHigh(metrics.usbStabilityPct, KPI_TARGETS.availabilityPct),
                        metricLevelGoodHigh(flowSuccessPct, KPI_TARGETS.flowSuccessPct),
                        metricLevelGoodLow(metrics.detachPerHour, KPI_TARGETS.detachPerHourMax),
                        metricLevelGoodHigh(metrics.wakeupOkPct, KPI_TARGETS.wakeupOkPctMin),
                        metricLevelGoodLow(recoverySecondsEstimate, KPI_TARGETS.recoverySeconds)
                      ].filter(v => v === "green").length}
                      /5
                    </p>
                    <p className="kpi-card__hint">objetivos clave cumplidos</p>
                  </div>
                </div>
              )}

              <div className="op-check-grid">
                {checks.map(c => (
                  <article key={c.id} className={`op-check-card op-check-card--${c.level}`}>
                    <div className="op-check-header">
                      <StatusMark level={c.level} size="md" />
                      <h3>{c.title}</h3>
                    </div>
                    <ul>
                      {c.verifiers.map(v => (
                        <li key={v}>{v}</li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>

              <h2 className="section-title" style={{ marginTop: "1.5rem" }}>
                Informe de sesión (último intento de venta)
              </h2>
              <p style={{ marginTop: 0, color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                La app envía un informe tras cada flujo SumUp (<code>POST /device/v1/session-report</code>
                ). Incluye diagnóstico automático y contexto USB/VEND. Requiere APK ≥ 7.0.7 y migración
                de BD.
              </p>
              {sessionReports === null ? (
                <p>Cargando informes…</p>
              ) : sessionReports.length === 0 ? (
                <p style={{ color: "var(--color-text-muted)" }}>
                  Sin informes aún (instalá el APK actualizado y ejecutá una venta de prueba).
                </p>
              ) : (
                <div
                  className="card card--padded"
                  style={{
                    marginBottom: "1rem",
                    borderLeft: "4px solid var(--color-accent)",
                    background: "var(--color-bg-elevated)"
                  }}
                >
                  <p style={{ margin: "0 0 0.5rem", fontWeight: 600 }}>Último informe</p>
                  <p style={{ margin: "0 0 0.35rem", fontSize: "0.9rem", color: "var(--color-text-secondary)" }}>
                    {formatDateTimeDMY(sessionReports[0].created_at)} ·{" "}
                    <code style={{ fontSize: "0.8rem" }}>{sessionReports[0].external_id || "—"}</code>
                  </p>
                  <p style={{ margin: "0.5rem 0", lineHeight: 1.45 }}>
                    {sessionReports[0].diagnosis_hint || "—"}
                  </p>
                  <details style={{ marginTop: "0.5rem", fontSize: "0.82rem" }}>
                    <summary style={{ cursor: "pointer", color: "var(--color-text-muted)" }}>
                      Ver JSON completo del informe
                    </summary>
                    <pre
                      style={{
                        marginTop: "0.5rem",
                        padding: "0.75rem",
                        overflow: "auto",
                        maxHeight: "280px",
                        background: "var(--color-bg)",
                        borderRadius: "var(--radius-sm)",
                        fontSize: "0.78rem"
                      }}
                    >
                      {JSON.stringify(sessionReports[0].report, null, 2)}
                    </pre>
                  </details>
                </div>
              )}

              <h2 className="section-title" style={{ marginTop: "1.5rem" }}>
                Eventos de terminal (pago / VEND)
              </h2>
              <p style={{ marginTop: 0, color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                Registros enviados por la app Android (<code>device/v1/events</code>): útil para ver
                rechazos SumUp (<em>Log in first…</em>), <code>vend_hold_sent</code>, etc. Se actualiza
                cada {Math.round(REFRESH_MS / 1000)} s.
              </p>
              {transactionEvents === null ? (
                <p>Cargando eventos…</p>
              ) : transactionEvents.length === 0 ? (
                <p style={{ color: "var(--color-text-muted)" }}>Sin eventos para este terminal.</p>
              ) : (
                <div className="table-wrap">
                  <table className="table-modern">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Tipo</th>
                        <th>Motivo</th>
                        <th>Ext. id</th>
                        <th>Payload (recorte)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactionEvents.map(row => {
                        let fullPayload = "";
                        if (row.payload != null) {
                          try {
                            fullPayload =
                              typeof row.payload === "string"
                                ? row.payload
                                : JSON.stringify(row.payload);
                          } catch {
                            fullPayload = String(row.payload);
                          }
                        }
                        const payloadDisplay =
                          fullPayload.length > 120 ? `${fullPayload.slice(0, 120)}…` : fullPayload;
                        const payloadTitle =
                          fullPayload.length > 120
                            ? fullPayload.length > 1800
                              ? `${fullPayload.slice(0, 1800)}…`
                              : fullPayload
                            : undefined;
                        return (
                          <tr key={row.id}>
                            <td>{formatDateTimeDMY(row.created_at)}</td>
                            <td>
                              <code style={{ fontSize: "0.78rem" }}>{row.event_type}</code>
                            </td>
                            <td>{row.reason || "—"}</td>
                            <td style={{ maxWidth: "120px", wordBreak: "break-all", fontSize: "0.78rem" }}>
                              {row.external_id || "—"}
                            </td>
                            <td
                              style={{ maxWidth: "280px", fontSize: "0.78rem", wordBreak: "break-word" }}
                              title={payloadTitle}
                            >
                              {payloadDisplay || "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default OperationsControl;
